// src/modules/units/tyres/shared/tyre-columns.js
//
// THE column registry for the tyre subsystem. One entry per field, and both the
// register table and the CSV export derive from it.
//
// Why this exists
// ---------------
// A tyre column used to be defined twice: COLUMNS in the register (10 entries,
// HTML renderers) and CSV_COLUMNS in the reports (22 entries, plain accessors).
// The two disagreed, and not only cosmetically — the table showed
// effectiveStatus() while the CSV exported the raw stored tyreStatus, so a tyre
// with a sensor and a stale IN_STOCK stamp read "Fitted" on screen and "IN_STOCK"
// in the spreadsheet. That is the same dual-source-of-truth that effectiveStatus()
// was introduced to remove, so it is removed here the same way:
// one definition, many consumers.
//
// The three tiers
// ---------------
// Each entry supplies a `value`, and may override how either consumer renders it:
//
//   value(t)   REQUIRED. The datum, in its most useful machine-readable form.
//              Returns null — never '' or 0 or an em dash — when the record
//              cannot answer. This is what the CSV writes.
//   format(v)  OPTIONAL. Turns the datum into display text for the table
//              ("12 345 km", "8 mm", "12 Mar 2026"). Table only.
//   render(t)  OPTIONAL. Full HTML for the table cell, for the handful of columns
//              that are a badge, a link or two stacked lines. Overrides format.
//
// The tiers are not ceremony. They are what stops the table's formatting reaching
// the spreadsheet: formatKm(12345) is "12 345 km" with a non-breaking space, which
// Excel imports as text, not a number. A single render()/value() pair invites
// exactly that mistake; a datum with a display transform on top cannot make it.
//
// `csvHeader` exists for the same reason in the other direction: the table puts
// the unit in the cell ("8 mm") so its heading stays short, while the CSV must
// keep the cell a bare number and therefore puts the unit in the heading
// ("Tread (mm)"). Same fact, two media, one entry.
//
// Availability vs default
// -----------------------
// Two different ideas that used to share the name `hideColumns`:
//
//   available(ctx)  a HARD constraint. Stock/Depot lists tyres that are by
//                   definition not fitted, so the fitment columns cannot be
//                   populated there — they are not offered at all. The column
//                   declares its own requirement, so a fourth fitment column
//                   inherits the rule instead of having to be added by hand to
//                   every preset that ought to drop it.
//   DEFAULT_COLUMN_KEYS  a SOFT default. What you see before you choose.
//                   The column picker overrides this; it can never override
//                   availability.
//
// Order lives in the two key arrays, not in a per-entry flag, because a boolean
// cannot express order and both consumers need their own.

import {
  esc, formatKm, formatTread, formatShortDate, formatMoney, formatTemperature,
  fitmentSensorId, fitmentUnitName, fitmentPosition
} from './tyre-format.js';
import {
  hasKnownStatus, tyreStatusLabel, tyreStatusTone
} from './tyre-status.js';
import { remainingTreadPct, underinflatedPct } from './tyre-metrics.js';
import { stageMetric, currentStageMetric } from './tyre-life-stages.js';

const isBlank = (v) => v === null || v === undefined || v === '';

// The subsystem's "no value" cell. Exported because the register's skeleton and
// state rows share the table's vocabulary.
export const MUTED = '<span class="tm-muted">—</span>';

/** Escape a display string into a table cell, or show the muted dash. */
export function cell(s) {
  return (s === null || s === undefined || s === '' || s === '—') ? MUTED : esc(s);
}

// Normalises anything falsy-but-meaningless to null so `value` keeps its contract.
const orNull = (v) => (isBlank(v) ? null : v);

// Suffix helpers for columns whose datum is a bare number the table wants labelled.
const pct = (v) => (v === null ? null : `${v}%`);

// Groups, in the order a picker should list them.
export const COLUMN_GROUPS = Object.freeze([
  'Identity',
  'Specification',
  'Condition',
  'Life stages',
  'Distance & pressure',
  'Fitment',
  'Commercial',
  'Admin'
]);

// A fitment column is only meaningful where a fitment can exist.
const needsFitment = (ctx) => ctx?.hasFitment !== false;

export const TYRE_COLUMNS = Object.freeze([
  // ---- Identity -----------------------------------------------------------
  {
    key: 'id', header: 'Tyre ID', group: 'Identity', cls: 'tm-col-id',
    // The row's identity and its only click target, so it cannot be removed and
    // it stays first. `locked` is read by resolveColumns() and (later) the picker.
    locked: true,
    value: (t) => orNull(t.externalTyreId),
    render: (t) => `<button type="button" class="tm-link" data-open="${esc(t.externalTyreId)}">${cell(t.externalTyreId)}</button>`
  },
  {
    key: 'serial', header: 'Serial', group: 'Identity',
    value: (t) => orNull(t.tyreSerialNumber)
  },
  {
    key: 'rfid', header: 'RFID', group: 'Identity',
    value: (t) => orNull(t.tyreRFID)
  },

  // ---- Specification ------------------------------------------------------
  {
    // A COMPOSITE, not a field: the table's long-standing two-line cell. It is
    // kept because it is the default look, and `brand` / `size` are registered
    // separately below so the picker can offer either one alone.
    key: 'spec', header: 'Brand / Size', group: 'Specification', cls: 'tm-col-spec',
    value: (t) => orNull([t.tyreBrand || t.tyreMake, t.tyreSize].filter(Boolean).join(' ')),
    render: (t) => {
      const brand = t.tyreBrand || t.tyreMake;
      if (!brand && !t.tyreSize) return MUTED;
      return `<span class="tm-cell-strong">${brand ? esc(brand) : '—'}</span>` +
        (t.tyreSize ? `<span class="tm-cell-sub">${esc(t.tyreSize)}</span>` : '');
    }
  },
  {
    // tyreMake is legacy and still carries values on older records; the fallback
    // matches what the composite cell has always done.
    key: 'brand', header: 'Brand', group: 'Specification',
    value: (t) => orNull(t.tyreBrand || t.tyreMake)
  },
  { key: 'model', header: 'Model', group: 'Specification', value: (t) => orNull(t.tyreModel) },
  { key: 'size', header: 'Size', group: 'Specification', value: (t) => orNull(t.tyreSize) },
  { key: 'type', header: 'Type', group: 'Specification', value: (t) => orNull(t.tyreType) },
  {
    key: 'treadPattern',
    header: 'Tread pattern',
    group: 'Specification',
    value: (t) => orNull(t.tyreTreadPattern)
  },
  { key: 'grade', header: 'Grade', group: 'Specification', value: (t) => orNull(t.tyreGrade) },
  {
    key: 'app', header: 'Application', group: 'Specification', cls: 'tm-col-app',
    value: (t) => orNull(t.tyreApplication)
  },

  // ---- Condition ----------------------------------------------------------
  {
    key: 'status', header: 'Status', group: 'Condition', cls: 'tm-col-status',
    // The DERIVED status, and the same label the badge shows. hasKnownStatus()
    // guards an empty seed record so an unknown status stays unknown in both
    // media rather than defaulting to "In Stock".
    value: (t) => (hasKnownStatus(t) ? tyreStatusLabel(t) : null),
    render: (t) => (hasKnownStatus(t)
      ? `<span class="tm-badge tm-tone-${tyreStatusTone(t)}">${esc(tyreStatusLabel(t))}</span>`
      : MUTED)
  },
  {
    // The raw stored enum, kept ALONGSIDE the derived Status label so a
    // spreadsheet can still filter or pivot on the code (IN_STOCK) even though
    // the label now reads "In Stock". This is the code; `status` above is the
    // derived label. Offerable as a table column too, but off by default.
    key: 'statusCode', header: 'Status code', group: 'Condition',
    value: (t) => orNull(t.tyreStatus)
  },
  {
    key: 'tread', header: 'Tread', csvHeader: 'Tread (mm)', group: 'Condition', cls: 'tm-col-num',
    value: (t) => orNull(t.tyreTreadDepth),
    format: formatTread
  },
  {
    key: 'initialTread', header: 'Initial tread', csvHeader: 'Initial tread (mm)',
    group: 'Condition', cls: 'tm-col-num',
    value: (t) => orNull(t.initialTyreTreadDepth),
    format: formatTread
  },
  {
    key: 'minRetreadTread',
    header: 'Retread limit',
    csvHeader: 'Minimum tread for retread (mm)',
    group: 'Condition',
    cls: 'tm-col-num',
    value: (t) => orNull(t.minimumTreadForRetread),
    format: formatTread
  },
  // Retread lifecycle. These are the only retread-specific fields on the tyre record
  // (retreadCount / maxRetreads) — the applied product, tread, grade, date and cost
  // live on the retread batch, not the tyre, so they can't be shown per-row without a
  // fetch per tyre; they'd need denormalising onto GET /tyre first. Off by default.
  {
    // How many times the casing has been retreaded. 0 is a real value (never
    // retreaded) so it is NOT nulled — only a missing field shows a dash.
    key: 'retreads', header: 'Retreads', csvHeader: 'Retread count',
    group: 'Condition', cls: 'tm-col-num',
    value: (t) => orNull(t.retreadCount)
  },
  {
    key: 'maxRetreads', header: 'Max retreads', csvHeader: 'Max retreads',
    group: 'Condition', cls: 'tm-col-num',
    value: (t) => orNull(t.maxRetreads)
  },
  {
    // Derived: cap minus count, never below zero. Needs both numbers, else a dash.
    key: 'retreadsLeft', header: 'Retreads left', csvHeader: 'Retreads remaining',
    group: 'Condition', cls: 'tm-col-num',
    value: (t) => {
      const max = Number(t.maxRetreads);
      const done = Number(t.retreadCount);
      if (!Number.isFinite(max) || !Number.isFinite(done)) return null;
      return Math.max(0, max - done);
    }
  },
  {
    key: 'expectedTread',
    header: 'Expected tread',
    csvHeader: 'Expected tread depth (mm)',
    group: 'Condition',
    cls: 'tm-col-num',
    value: (t) => orNull(t.expectedTreadDepth),
    format: formatTread
  },
  {
    key: 'treadPct', header: 'Tread left', csvHeader: 'Tread remaining %',
    group: 'Condition', cls: 'tm-col-num',
    value: (t) => { const p = remainingTreadPct(t); return p === null ? null : Math.round(p); },
    format: pct
  },
  {
    key: 'wear', header: 'Wear pattern', group: 'Condition',
    value: (t) => orNull(t.tyreWearPattern)
  },
  {
    key: 'treadUpdated', header: 'Tread updated', group: 'Condition', cls: 'tm-col-updated',
    value: (t) => orNull(t.tyreTreadDepthUpdatedAt),
    format: formatShortDate
  },

  // ---- Life stages -------------------------------------------------------
  // The list endpoint returns the same compact retreadHistory array as detail.
  // These columns therefore need no extra per-row request and remain sortable.
  {
    key: 'currentStageKm', header: 'Current-stage km', csvHeader: 'Current stage distance (km)',
    group: 'Life stages', cls: 'tm-col-num',
    value: (t) => currentStageMetric(t, 'tyreKm'),
    format: formatKm
  },
  {
    key: 'newStageKm', header: 'New stage km', csvHeader: 'New stage distance (km)',
    group: 'Life stages', cls: 'tm-col-num',
    value: (t) => stageMetric(t, 0, 'tyreKm'),
    format: formatKm
  },
  {
    key: 'firstCapKm', header: 'First cap km', csvHeader: 'First cap distance (km)',
    group: 'Life stages', cls: 'tm-col-num',
    value: (t) => stageMetric(t, 1, 'tyreKm'),
    format: formatKm
  },
  {
    key: 'secondCapKm', header: 'Second cap km', csvHeader: 'Second cap distance (km)',
    group: 'Life stages', cls: 'tm-col-num',
    value: (t) => stageMetric(t, 2, 'tyreKm'),
    format: formatKm
  },
  {
    key: 'thirdCapKm', header: 'Third cap km', csvHeader: 'Third cap distance (km)',
    group: 'Life stages', cls: 'tm-col-num',
    value: (t) => stageMetric(t, 3, 'tyreKm'),
    format: formatKm
  },
  {
    key: 'currentStageKmPerMm', header: 'Current km/mm', csvHeader: 'Current stage km per mm',
    group: 'Life stages', cls: 'tm-col-num',
    value: (t) => currentStageMetric(t, 'kmPerMm'),
    format: (v) => (v === null ? null : `${Math.round(Number(v)).toLocaleString('en-ZA')} km/mm`)
  },
  {
    key: 'currentStageCostPerKm', header: 'Current cost/km', csvHeader: 'Current stage cost per km',
    group: 'Life stages', cls: 'tm-col-num',
    value: (t) => currentStageMetric(t, 'costPerKm'),
    format: (v) => (v === null ? null : `R ${formatMoney(v)}/km`)
  },

  // ---- Distance & pressure ------------------------------------------------
  {
    key: 'manufacturerMileage',
    header: 'Expected mileage',
    csvHeader: 'Manufacturer expected mileage (km)',
    group: 'Distance & pressure',
    cls: 'tm-col-num',
    value: (t) => orNull(t.manufacturerExpectedMileage),
    format: formatKm
  },
  {
    key: 'distance', header: 'Distance', csvHeader: 'Distance (km)',
    group: 'Distance & pressure', cls: 'tm-col-num',
    value: (t) => orNull(t.tyreKm),
    format: formatKm
  },
  {
    key: 'estimatedKmRemaining',
    header: 'Km remaining',
    csvHeader: 'Estimated km remaining',
    group: 'Distance & pressure',
    cls: 'tm-col-num',
    value: (t) => orNull(t.estimatedKmRemaining),
    format: formatKm
  },
  {
    key: 'underPct', header: 'Under-inflated', csvHeader: 'Under-inflated %',
    group: 'Distance & pressure', cls: 'tm-col-num',
    value: (t) => { const p = underinflatedPct(t); return p === null ? null : Math.round(p); },
    format: pct
  },
  {
    key: 'highTemp', header: 'Highest temp', csvHeader: 'Highest temperature (C)',
    group: 'Distance & pressure', cls: 'tm-col-num',
    value: (t) => orNull(t.highestTemperature),
    format: formatTemperature
  },

  // ---- Fitment ------------------------------------------------------------
  // The blank argument is null here, not the screens' em dash: the datum is
  // "unknown", and each consumer decides how to draw unknown. That is what
  // retires the blank-threading (fitmentSensorId(t, '')) at the CSV call site.
  {
    key: 'sensor', header: 'Sensor', group: 'Fitment', cls: 'tm-col-sensor',
    available: needsFitment,
    value: (t) => fitmentSensorId(t, null)
  },
  {
    key: 'unit', header: 'Unit', group: 'Fitment', cls: 'tm-col-unit',
    available: needsFitment,
    value: (t) => fitmentUnitName(t, null)
  },
  {
    key: 'position', header: 'Position', group: 'Fitment', cls: 'tm-col-pos',
    available: needsFitment,
    value: (t) => fitmentPosition(t, null)
  },
  {
    // Where the tyre lives. Grouped with fitment because it reads as part of
    // "where is this tyre", but unlike sensor/unit/position it has NO fitment
    // guard — a stock tyre has a yard too. Prefer the denormalised name; fall
    // back to the id so a record with a yard but no name still shows something.
    key: 'yard', header: 'Yard', group: 'Fitment', cls: 'tm-col-unit',
    value: (t) => orNull(t.yardName || t.yardId)
  },

  // ---- Commercial ---------------------------------------------------------
  {
    key: 'purchaseCost',
    header: 'Purchase cost excl VAT',
    csvHeader: 'Purchase cost excl VAT',
    group: 'Commercial',
    cls: 'tm-col-num',
    value: (t) => orNull(t.purchaseCost),
    format: formatMoney
  },
  {
    key: 'supplier',
    header: 'Supplier',
    group: 'Commercial',
    value: (t) => orNull(t.supplierName)
  },
  {
    key: 'purchaseDate',
    header: 'Invoice date',
    group: 'Commercial',
    cls: 'tm-col-updated',
    value: (t) => orNull(t.purchaseDate),
    format: formatShortDate
  },

  // ---- Admin --------------------------------------------------------------
  {
    key: 'updated', header: 'Updated', group: 'Admin', cls: 'tm-col-updated',
    value: (t) => orNull(t.updatedAt || t.tyreKmUpdatedAt),
    format: formatShortDate
  },
  { key: 'notes', header: 'Notes', group: 'Admin', value: (t) => orNull(t.notes) }
]);

// The register's default columns, in order. Exactly what the table showed before
// the registry existed; the picker will override this per user, never replace it.
export const DEFAULT_COLUMN_KEYS = Object.freeze([
  'id',
  'status',
  'spec',
  'grade',
  'tread',
  'currentStageKm',
  'distance',
  'estimatedKmRemaining',
  'sensor',
  'unit',
  'position',
  'updated'
]);

// The CSV export set, in order.
//
// `spec` is deliberately absent: it is a presentation composite, and `brand` and
// `size` carry the same two facts in separate, sortable columns.
//
// model / grade / wear are NEW here — the create form has always captured them and
// the export could not emit them. They sit in their logical places; if a
// downstream script reads this file by column position, move the three keys to
// the end of this array and the old positions are restored.
export const CSV_COLUMN_KEYS = Object.freeze([
  'id',
  'status',
  'statusCode',

  'brand',
  'size',
  'type',
  'treadPattern',
  'grade',
  'app',
  'serial',

  'tread',
  'initialTread',
  'minRetreadTread',
  'retreads',
  'maxRetreads',
  'retreadsLeft',
  'expectedTread',
  'treadPct',
  'wear',

  'currentStageKm',
  'newStageKm',
  'firstCapKm',
  'secondCapKm',
  'thirdCapKm',
  'currentStageKmPerMm',
  'currentStageCostPerKm',

  'manufacturerMileage',
  'distance',
  'estimatedKmRemaining',

  'underPct',

  'purchaseCost',
  'supplier',
  'purchaseDate',

  'sensor',
  'unit',
  'position',
  'yard',
  'updated'
]);

const BY_KEY = new Map(TYRE_COLUMNS.map((c) => [c.key, c]));

/** One column by key, or undefined. */
export function getColumn(key) {
  return BY_KEY.get(key);
}

/** True when this column can be populated in the given context. */
export function isAvailable(col, ctx) {
  return typeof col?.available === 'function' ? !!col.available(ctx) : true;
}

/** Every column offerable in this context, catalogue order (what a picker lists). */
export function availableColumns(ctx) {
  return TYRE_COLUMNS.filter((c) => isAvailable(c, ctx));
}

/**
 * Turn an ordered list of keys into an ordered list of columns for the TABLE.
 *
 * Validates on read, which is the discipline that keeps a stored choice working
 * after the registry changes shape:
 *   - unknown keys are dropped (a column was renamed or removed since it was saved)
 *   - duplicates are dropped
 *   - unavailable columns are dropped (the hard constraint)
 *   - locked columns are re-inserted at the front if a stored list has lost them
 * Falls back to the defaults if nothing survives, so the table can never render
 * headerless.
 */
export function resolveColumns(keys = DEFAULT_COLUMN_KEYS, ctx = {}) {
  // The fallback is decided BEFORE locked columns are re-inserted. Injecting them
  // first would leave a corrupt or empty stored list rendering as a one-column
  // table of nothing but Tyre ID — technically non-empty, useless in practice.
  let picked = pick(keys, ctx);
  if (!picked.length) picked = pick(DEFAULT_COLUMN_KEYS, ctx);

  const seen = new Set(picked.map((c) => c.key));
  TYRE_COLUMNS.forEach((col, i) => {
    if (!col.locked || seen.has(col.key) || !isAvailable(col, ctx)) return;
    seen.add(col.key);
    picked.splice(Math.min(i, picked.length), 0, col);
  });

  return picked;
}

// Keys -> columns, dropping unknowns, duplicates and anything this mode cannot populate.
function pick(keys, ctx) {
  const seen = new Set();
  const out = [];
  for (const key of keys) {
    if (seen.has(key)) continue;
    const col = BY_KEY.get(key);
    if (!col || !isAvailable(col, ctx)) continue;
    seen.add(key);
    out.push(col);
  }
  return out;
}

/** The CSV export's columns, in export order. */
export function csvColumns() {
  return CSV_COLUMN_KEYS.map((k) => BY_KEY.get(k)).filter(Boolean);
}

/** The heading this column uses in the given medium. */
export function columnHeader(col, medium = 'table') {
  return (medium === 'csv' && col.csvHeader) ? col.csvHeader : col.header;
}

/** Display text for a table cell, before escaping. null when there is nothing to show. */
export function columnText(col, tyre) {
  const v = col.value(tyre);
  if (!col.format) return v;
  const out = col.format(v, tyre);
  // The existing formatters answer a missing value with an em dash; cell() maps
  // that back to the muted span, so both conventions land in the same place.
  return out;
}

/** HTML for a table cell. */
export function columnHtml(col, tyre) {
  return col.render ? col.render(tyre) : cell(columnText(col, tyre));
}

/** The scalar the CSV writes for this column. */
export function columnCsv(col, tyre) {
  return col.value(tyre);
}
